// Connection Progress Bar (Feb 2026).
//
// Visual multi-node stepper for the Match → Meet → Keep journey. Sits above
// the LifecyclePill in the chat header so users can see the WHOLE trajectory
// at a glance, not just the current stage.
//
//   ●──────●──────○──────○      (Matched · Meeting · Met · Kept)
//   Aug 12  Aug 14
//
// Each node:
//   • Completed stages → filled circle in the stage color + date under label
//   • Current stage    → filled circle with a subtle pulse ring
//   • Future stages    → hollow ring in muted grey
// After keep_cycle > 1 the "Kept" node shows a ×N badge indicating the loop.

import { Heart, MapPin, CheckCircle2, Handshake } from 'lucide-react';

// Stage ladder. `Matched` is implicit (every match starts there); we render
// 4 explicit nodes so the bar tells the "matched → planned → met → kept" story
// in one glance. Feb 26 2026 · The middle node is Planned (mutually accepted
// date), NOT Meeting. Mutual meet intent alone shows Planned ○ — only when
// both have accepted a specific date does Planned ✓.
const NODES = [
  { key: 'matched',       label: 'Matched',   icon: Handshake,     color: '#8A8A93',
    completedWhen: (l) => true },                              // always started
  { key: 'planned',       label: 'Planned',   icon: MapPin,        color: '#B38138',
    activeWhen:    (l) => l.lifecycle_stage === 'meet_pending'
                          || l.lifecycle_stage === 'ready_to_plan'
                          || l.lifecycle_stage === 'date_pending',
    completedWhen: (l) => !!l.mutual_date_planned_at
                          || l.lifecycle_stage === 'planned'
                          || l.lifecycle_stage === 'met'
                          || l.lifecycle_stage === 'kept',
    dateFrom:      (l) => l.mutual_date_planned_at },
  { key: 'met',           label: 'Met',       icon: CheckCircle2,  color: '#15803D',
    activeWhen:    (l) => l.lifecycle_stage === 'planned',
    completedWhen: (l) => !!l.mutual_met_at || l.lifecycle_stage === 'met'
                          || l.lifecycle_stage === 'kept',
    dateFrom:      (l) => l.mutual_met_at },
  { key: 'kept',          label: 'Kept',      icon: Heart,         color: '#B91C1C',
    activeWhen:    (l) => l.lifecycle_stage === 'met',
    completedWhen: (l) => !!l.mutual_keep_at,
    dateFrom:      (l) => l.mutual_keep_at },
];

function _fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return null; }
}

export default function ConnectionProgressBar({ lifecycle, compact = false }) {
  if (!lifecycle || lifecycle.lifecycle_stage === 'archived') return null;

  return (
    <div className={`w-full flex items-start ${compact ? 'gap-1' : 'gap-2'}`}
         data-testid="connection-progress-bar">
      {NODES.map((node, idx) => {
        const done   = node.completedWhen(lifecycle);
        const active = node.activeWhen ? node.activeWhen(lifecycle) : false;
        const dateStr = done && node.dateFrom ? _fmtDate(node.dateFrom(lifecycle)) : null;
        const Icon = node.icon;
        // Segment: node circle + connector to next
        return (
          <div key={node.key}
               data-testid={`progress-node-${node.key}${active ? '-active' : done ? '-done' : '-todo'}`}
               className="flex-1 flex flex-col items-center min-w-0">
            <div className="flex items-center w-full">
              {/* Left connector — hidden on first node */}
              {idx > 0 && (
                <div className="flex-1 h-[2px] transition-colors"
                     style={{ backgroundColor: done ? node.color : '#E5E5EA' }} />
              )}
              {/* Node dot */}
              <div className="relative flex items-center justify-center flex-shrink-0"
                   style={{ width: compact ? 20 : 24, height: compact ? 20 : 24 }}>
                {active && (
                  <span className="absolute inset-0 rounded-full animate-ping opacity-40"
                        style={{ backgroundColor: node.color }} />
                )}
                <span className={`relative rounded-full flex items-center justify-center transition-all
                                  ${done ? 'shadow-sm' : ''}`}
                      style={{
                        width:  compact ? 18 : 22,
                        height: compact ? 18 : 22,
                        backgroundColor: done ? node.color : 'white',
                        border: `2px solid ${done ? node.color : '#D4D4D8'}`,
                      }}>
                  {done && <Icon size={compact ? 9 : 11} strokeWidth={2.6} color="white" />}
                </span>
                {/* Multi-Keep loop badge on the Kept node */}
                {node.key === 'kept' && lifecycle.keep_cycle > 1 && (
                  <span className="absolute -top-1 -right-2 rounded-full text-white text-[8px] font-bold px-1 py-[1px]"
                        style={{ backgroundColor: node.color, minWidth: 14, textAlign: 'center' }}>
                    ×{lifecycle.keep_cycle}
                  </span>
                )}
              </div>
              {/* Right connector — hidden on last node */}
              {idx < NODES.length - 1 && (
                <div className="flex-1 h-[2px] transition-colors"
                     style={{
                       backgroundColor: NODES[idx + 1].completedWhen(lifecycle) ? NODES[idx + 1].color : '#E5E5EA',
                     }} />
              )}
            </div>
            {/* Label + date */}
            {!compact && (
              <>
                <p className={`text-[10px] font-semibold tracking-tight mt-1.5 leading-tight
                              ${active ? '' : done ? '' : 'text-[#8A8A93]'}`}
                   style={active || done ? { color: node.color } : undefined}>
                  {node.label}
                </p>
                {dateStr && (
                  <p className="text-[9px] text-[#8A8A93] tracking-wider uppercase font-medium leading-tight mt-0.5">
                    {dateStr}
                  </p>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
